import React, { useState, useEffect } from 'react';
import ReactAudioPlayer from 'react-audio-player';
import { Link2, Unlink, Download, Pencil, Reply, Forward, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MarkdownText from "./MarkdownText";
import { BACKEND_API_URL } from "../utils/api";
import { filterMentionSuggestions } from "@/utils/mentionUtils";
import axios from 'axios';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import * as nodeEmoji from 'node-emoji';

// Map text emoticons to Unicode emojis (same as LinkifyTypography)
const emoticonToEmoji = {
    ':)': '😊',
    ':-)': '😊',
    ':(': '😢',
    ':-(': '😢',
    ':D': '😃',
    ':-D': '😃',
    '<3': '❤️',
    ':P': '😛',
    ':-P': '😛',
    ';)': '😉',
    ';-)': '😉',
    ':O': '😮',
    ':-O': '😮',
    ':|': '😐',
    ':-|': '😐',
    ':/': '😕',
    ':-/': '😕',
    ':\\': '😕',
    ':-\\': '😕',
    ':*': '😘',
    ':-*': '😘',
    '>:(': '😠',
    '>:-(': '😠',
    ':\'(': '😭',
    ':\'-(': '😭',
    'XD': '😆',
    'xD': '😆',
    'xP': '😝',
    'XP': '😝',
};

// Detect if a line looks like ASCII art or a diagram that needs space preservation
// Returns true if the line contains patterns typical of ASCII art
const isAsciiArtLine = (line) => {
    // Check for vertical line characters (common in diagrams) - any line with | is likely ASCII art
    if (/\|/.test(line)) return true;

    // Check for horizontal lines made of repeated characters (=== or --- with 4+ chars)
    if (/[=]{4,}|[-]{4,}/.test(line)) return true;

    // Check for box-drawing patterns like [Text] with significant spacing between brackets
    if (/\[.+\].*  .*\[.+\]/.test(line)) return true;

    // Check for tree/hierarchy drawing characters
    if (/[├└┌┐│─┬┴┼]/.test(line)) return true;

    // Check for lines that are mostly whitespace with bracketed text (diagram headers/nodes)
    if (/^\s*\[.+\]\s*$/.test(line) && /  /.test(line)) return true;

    return false;
};

// Comprehensive emoji conversion function that handles all Unicode emojis
// First tries nodeEmoji.unemojify, then encodes any remaining emojis as [U+XXXX] format
// This preserves all emojis while preventing database encoding errors
// Preserves newlines and other whitespace
const convertEmojisToShortcodes = (text) => {
    if (!text || typeof text !== 'string') return text || '';

    try {
        // First, try nodeEmoji.unemojify for known emojis (converts to :shortcode: format)
        let converted = nodeEmoji.unemojify(text);

        // Comprehensive emoji regex covering all Unicode emoji ranges
        // Includes: Miscellaneous Symbols (2600-26FF), Geometric Shapes Extended (1F780-1F7FF), etc.
        const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FAFF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F780}-\u{1F7FF}]|[\u{1FA70}-\u{1FAFF}]/gu;

        // Instead of removing unrecognized emojis, encode them as [U+XXXX] format
        // This preserves the emoji information while making it database-safe
        converted = converted.replace(emojiRegex, (match) => {
            // Get the Unicode code point(s) for the emoji
            // Handle multi-codepoint emojis (like flags or skin tone modifiers)
            let codePoints = [];
            for (let i = 0; i < match.length; i++) {
                const codePoint = match.codePointAt(i);
                if (codePoint >= 0x1F300) { // Only encode actual emoji ranges
                    codePoints.push(codePoint.toString(16).toUpperCase().padStart(4, '0'));
                    // Skip the next character if it's a surrogate pair
                    if (codePoint > 0xFFFF) i++;
                } else {
                    // Non-emoji character, keep as-is
                    return match[i];
                }
            }
            // Encode as [U+XXXX] format for single emoji or [U+XXXX][U+YYYY] for multi-codepoint
            return codePoints.map(cp => `[U+${cp}]`).join('');
        });

        // Clean up multiple spaces but preserve ASCII art, list indentation, and code blocks
        const lines = converted.split('\n');
        const processedLines = lines.map(line => {
            // ASCII art detection - preserve all spaces for diagram lines
            if (isAsciiArtLine(line)) {
                return line; // Preserve exactly as-is
            }

            // Check if line starts with list marker (preserve indentation)
            const listMatch = line.match(/^(\s*)([-*+]|\d+\.)\s/);
            if (listMatch) {
                // This is a list item - preserve leading indentation, only collapse spaces after the marker
                const [, indent, marker] = listMatch;
                const content = line.substring(listMatch[0].length);
                // Collapse multiple spaces in content only
                const cleanedContent = content.replace(/[ \t]{2,}/g, ' ');
                return indent + marker + ' ' + cleanedContent;
            }
            // Check if line is indented code or preserves structure
            if (/^\s{2,}/.test(line)) {
                // Heavily indented line - might be code or nested content, preserve indentation
                const match = line.match(/^(\s+)(.*)$/);
                if (match) {
                    const [, indent, content] = match;
                    // Only collapse spaces in content, preserve leading indent
                    const cleanedContent = content.replace(/[ \t]{2,}/g, ' ');
                    return indent + cleanedContent;
                }
            }
            // Regular line - collapse multiple spaces/tabs
            return line.replace(/[ \t]+/g, ' ');
        });

        return processedLines.join('\n');
    } catch (error) {
        console.warn('Error converting emojis:', error);
        // If conversion fails, try to encode emojis directly as [U+XXXX] format
        try {
            const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FAFF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F780}-\u{1F7FF}]|[\u{1FA70}-\u{1FAFF}]/gu;
            const encoded = text.replace(emojiRegex, (match) => {
                let codePoints = [];
                for (let i = 0; i < match.length; i++) {
                    const codePoint = match.codePointAt(i);
                    if (codePoint >= 0x1F300) {
                        codePoints.push(codePoint.toString(16).toUpperCase().padStart(4, '0'));
                        if (codePoint > 0xFFFF) i++;
                    } else {
                        return match[i];
                    }
                }
                return codePoints.map(cp => `[U+${cp}]`).join('');
            });
            // Preserve ASCII art and list indentation in fallback encoding too
            const lines = encoded.split('\n');
            const processedLines = lines.map(line => {
                // ASCII art detection - preserve all spaces for diagram lines
                if (isAsciiArtLine(line)) {
                    return line;
                }
                const listMatch = line.match(/^(\s*)([-*+]|\d+\.)\s/);
                if (listMatch) {
                    const [, indent, marker] = listMatch;
                    const content = line.substring(listMatch[0].length);
                    const cleanedContent = content.replace(/[ \t]{2,}/g, ' ');
                    return indent + marker + ' ' + cleanedContent;
                }
                if (/^\s{2,}/.test(line)) {
                    const match = line.match(/^(\s+)(.*)$/);
                    if (match) {
                        const [, indent, content] = match;
                        const cleanedContent = content.replace(/[ \t]{2,}/g, ' ');
                        return indent + cleanedContent;
                    }
                }
                return line.replace(/[ \t]+/g, ' ');
            });
            return processedLines.join('\n');
        } catch (e) {
            console.error('Failed to encode emojis:', e);
            // Last resort: remove emojis to prevent database errors
            const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FAFF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F780}-\u{1F7FF}]|[\u{1FA70}-\u{1FAFF}]/gu;
            return text.replace(emojiRegex, '').replace(/[ \t]+/g, '') || '';
        }
    }
};

// Convert text emoticons and shortcodes to actual emojis for display
// Also decodes [U+XXXX] sequences back to emojis
const convertToEmojis = (text) => {
    if (!text) return text;
    let result = text;

    // First, decode [U+XXXX] sequences back to emojis (for unrecognized emojis)
    // This handles emojis that were encoded because they don't have shortcodes
    // Handle multiple consecutive [U+XXXX] sequences for multi-codepoint emojis
    result = result.replace(/\[U\+([0-9A-F]{4,})\](?:\[U\+([0-9A-F]{4,})\])*/gi, (match) => {
        try {
            // Extract all code points from the match
            const codePoints = [];
            const regex = /\[U\+([0-9A-F]{4,})\]/gi;
            let m;
            while ((m = regex.exec(match)) !== null) {
                codePoints.push(parseInt(m[1], 16));
            }
            // Combine all code points into a single emoji (for multi-codepoint emojis like flags)
            return String.fromCodePoint(...codePoints);
        } catch (e) {
            console.warn('Failed to decode emoji codes:', match, e);
            return match; // Return original if decoding fails
        }
    });

    // Then, convert text emoticons to emojis (e.g., :) → 😊)
    // Sort by length descending to match longer emoticons first (e.g., :-) before :))
    const sortedEmoticons = Object.entries(emoticonToEmoji).sort((a, b) => b[0].length - a[0].length);
    for (const [emoticon, emoji] of sortedEmoticons) {
        // Use word boundary-like matching to avoid partial replacements
        const escaped = emoticon.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(^|\\s)${escaped}(\\s|$)`, 'g');
        result = result.replace(regex, `$1${emoji}$2`);
    }

    // Finally, convert shortcodes to emojis (e.g., :rocket: → 🚀)
    result = nodeEmoji.emojify(result);

    return result;
};

import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetFooter,
} from "@/components/ui/sheet";

const MessageCard = ({
    message,
    isThread,
    prepareActiveThread,
    updateMessageStatus,
    openFullscreenImage,
    user,
    selectedMessageId,
    onDelete,
    isSelected = false,
    isActiveThread = false,
    isThreadOpen = false,
    onSelect,
    showAuthor = true,
    showAvatar = false,
    onFocus,
    isFocused = false,
    mobileScreen = false,
    onReply = null,
    isReplySelected = false,
    onScrollToMessage = null
}) => {
    const navigate = useNavigate();
    const allChannels = useSelector((state) => state.channel.allChannels || []);

    const [, setUpdateTrigger] = useState(0);
    const [longPressTimer, setLongPressTimer] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedMessage, setEditedMessage] = useState(message.message);
    const [isUpdating, setIsUpdating] = useState(false);

    // Mention states for editing
    const [mentionQuery, setMentionQuery] = useState('');
    const [mentionSuggestions, setMentionSuggestions] = useState([]);
    const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
    const [mentionStartIndex, setMentionStartIndex] = useState(-1);
    const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
    const editInputRef = React.useRef(null);

    // Helper function to get full media URL
    // In production, use hardcoded URL for media files served from onemap.morphlelabs.com
    const PRODUCTION_MEDIA_URL = 'https://onemap.morphlelabs.com';

    const getMediaUrl = (url) => {
        if (!url) return url;
        if (url.startsWith('/')) {
            return `${PRODUCTION_MEDIA_URL}${url}`;
        }
        return url;
    };

    // Update relative time every minute
    useEffect(() => {
        const interval = setInterval(() => {
            setUpdateTrigger(prev => prev + 1);
        }, 60000);
        return () => clearInterval(interval);
    }, []);

    // Fetch users for mention suggestions when editing
    useEffect(() => {
        const fetchUsers = async () => {
            if (mentionQuery.length > 0 && isEditing) {
                try {
                    const accessToken = Cookies.get('access_token');
                    const response = await axios.get(`${BACKEND_API_URL}/api/users_and_tags/`, {
                        headers: {
                            'Authorization': `Token ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                    });

                    const filtered = filterMentionSuggestions(response.data.users, mentionQuery);

                    setMentionSuggestions(filtered);
                    setShowMentionSuggestions(filtered.length > 0);
                    setSelectedMentionIndex(0);
                } catch (error) {
                    console.error('Error fetching users:', error);
                }
            } else {
                setMentionSuggestions([]);
                setShowMentionSuggestions(false);
            }
        };

        fetchUsers();
    }, [mentionQuery, isEditing]);

    // Helper function to download media
    const handleDownload = async (url, filename) => {
        const fullUrl = getMediaUrl(url);
        try {
            const response = await fetch(fullUrl);
            if (!response.ok) {
                throw new Error('Fetch failed');
            }
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Download via fetch failed, trying direct link:', error);
            // Fallback: open in new tab for download
            const link = document.createElement('a');
            link.href = fullUrl;
            link.download = filename || 'download';
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    // Helper function to format file size
    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
        return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    };

    // Helper function to extract file name from URL
    const getFileName = (url) => {
        if (!url) return 'File';
        return url.split('/').pop()?.split('?')[0] || 'File';
    };

    // Helper function to get file extension
    const getFileExtension = (url) => {
        const fileName = getFileName(url);
        return fileName.split('.').pop()?.toUpperCase() || '';
    };

    // Helper function to get file type icon color and label
    const getFileTypeInfo = (url) => {
        const ext = getFileExtension(url).toLowerCase();
        if (ext === 'pdf') {
            return { color: 'bg-red-500', text: 'PDF' };
        } else if (['doc', 'docx'].includes(ext)) {
            return { color: 'bg-blue-600', text: 'DOC' };
        } else if (['xls', 'xlsx'].includes(ext)) {
            return { color: 'bg-green-600', text: 'XLS' };
        } else if (['ppt', 'pptx'].includes(ext)) {
            return { color: 'bg-orange-500', text: 'PPT' };
        } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
            return { color: 'bg-yellow-600', text: 'ZIP' };
        } else if (['txt', 'md', 'json', 'xml', 'csv'].includes(ext)) {
            return { color: 'bg-gray-500', text: ext.toUpperCase() };
        }
        return { color: 'bg-gray-500', text: ext.toUpperCase() || 'FILE' };
    };

    // Helper function to get relative time
    const getRelativeTime = (timestamp) => {
        const now = new Date();
        const messageDate = new Date(timestamp);
        const diffMs = now - messageDate;
        const diffSecs = Math.floor(diffMs / 1000);
        const diffMins = Math.floor(diffSecs / 60);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);
        const diffMonths = Math.floor(diffDays / 30);
        const diffYears = Math.floor(diffDays / 365);

        if (diffSecs < 60) return `${diffSecs}s ago`;
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 30) return `${diffDays}d ago`;
        if (diffMonths < 12) return `${diffMonths}mo ago`;
        return `${diffYears}y ago`;
    };

    // Helper function to check if message was edited
    const isMessageEdited = () => {
        const createdTime = new Date(message.created_at).getTime();
        const updatedTime = new Date(message.updated_at).getTime();
        return (updatedTime - createdTime) > 1000;
    };

    const isOwnMessage = message.author.username === user?.username;
    const messageAlignment = isOwnMessage ? 'justify-end' : 'justify-start';
    const flexDirection = isOwnMessage ? 'flex-row-reverse' : 'flex-row';

    // Handle card click - opens thread on single click
    const handleCardClick = (event) => {
        if (onSelect && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            event.stopPropagation();
            onSelect(message, event);
        } else if (!isThread && prepareActiveThread) {
            // Open thread on single click
            const threadId = message.root_thread ?? message.thread ?? -1;
            prepareActiveThread(threadId, message.id);
        }
        if (onFocus) {
            onFocus(message);
        }
    };

    // Handle author click - navigate to user's personal channel
    const handleAuthorClick = (e) => {
        e.stopPropagation();
        e.preventDefault();

        const authorId = message.author?.id;
        if (!authorId) {
            toast.error('Unable to find user information');
            return;
        }

        // Find the personal channel for this author
        const personalChannel = allChannels.find(channel => {
            if (!channel.is_personalized) return false;
            // Handle created_by as either an id or an object with id
            const creatorId = typeof channel.created_by === 'object'
                ? channel.created_by?.id
                : channel.created_by;
            return creatorId && String(creatorId) === String(authorId);
        });

        if (personalChannel) {
            navigate(`/channels/${personalChannel.id}`);
        } else {
            toast.error('Unable to find personal channel');
        }
    };

    // Handle reply click (only for thread messages)
    const handleReplyClick = (e) => {
        e.stopPropagation();
        if (isThread && onReply) {
            onReply(message);
        }
    };

    // Touch handlers for mobile
    const handleTouchStart = () => {
        const timer = setTimeout(() => {
            if (!isThread && prepareActiveThread) {
                const threadId = message.root_thread ?? message.thread ?? -1;
                prepareActiveThread(threadId, message.id);
            }
        }, 500);
        setLongPressTimer(timer);
    };

    const handleTouchEnd = () => {
        if (longPressTimer) {
            clearTimeout(longPressTimer);
            setLongPressTimer(null);
        }
    };

    const handleTouchMove = () => {
        if (longPressTimer) {
            clearTimeout(longPressTimer);
            setLongPressTimer(null);
        }
    };

    const handleEditClick = (e) => {
        e.stopPropagation();
        setIsEditing(true);
        setEditedMessage(message.message);
        setMentionQuery('');
        setMentionSuggestions([]);
        setShowMentionSuggestions(false);
        setMentionStartIndex(-1);
        setSelectedMentionIndex(0);
    };

    const handleMentionSelect = (user) => {
        const textBefore = editedMessage.substring(0, mentionStartIndex);
        const textAfter = editedMessage.substring(mentionStartIndex).replace(/@[^\s@]*/, `@${user.username} `);
        setEditedMessage(textBefore + textAfter);
        setShowMentionSuggestions(false);
        setMentionQuery('');
        setTimeout(() => {
            if (editInputRef.current) {
                editInputRef.current.focus();
            }
        }, 0);
    };

    const handleEditTextChange = (e) => {
        const newValue = e.target.value;
        setEditedMessage(newValue);

        // Check for @ mention
        const cursorPos = e.target.selectionStart || newValue.length;
        const textBeforeCursor = newValue.substring(0, cursorPos);
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');

        if (lastAtIndex !== -1) {
            const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
            if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
                setMentionStartIndex(lastAtIndex);
                setMentionQuery(textAfterAt);
            } else {
                setShowMentionSuggestions(false);
                setMentionQuery('');
            }
        } else {
            setShowMentionSuggestions(false);
            setMentionQuery('');
        }
    };

    const handleEditKeyDown = (e) => {
        if (showMentionSuggestions && mentionSuggestions.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedMentionIndex(prev =>
                    prev < mentionSuggestions.length - 1 ? prev + 1 : prev
                );
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedMentionIndex(prev => prev > 0 ? prev - 1 : prev);
            } else if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                const selectedUser = mentionSuggestions[selectedMentionIndex];
                if (selectedUser) handleMentionSelect(selectedUser);
            } else if (e.key === 'Escape') {
                setShowMentionSuggestions(false);
                setMentionQuery('');
            }
        } else if (e.key === 'Enter' && !e.shiftKey) {
            // Save on Enter (unless mention suggestions are showing or Shift is held)
            e.preventDefault();
            handleSaveEdit();
        }
        // Shift+Enter allows new line (default behavior)
    };

    const handleSaveEdit = async () => {
        if (!editedMessage.trim()) {
            toast.error('Message cannot be empty');
            return;
        }

        // Convert both to shortcodes for comparison (backend stores as shortcodes)
        const editedMessageShortcode = convertEmojisToShortcodes(editedMessage);

        // Compare shortcode versions to avoid false positives
        if (editedMessageShortcode === message.message) {
            setIsEditing(false);
            return;
        }

        setIsUpdating(true);
        try {
            const accessToken = Cookies.get('access_token');
            // Convert emojis to shortcodes for database storage
            const messageToSend = convertEmojisToShortcodes(editedMessage);
            const response = await axios.patch(
                `${BACKEND_API_URL}/api/message/${message.id}/edit/`,
                { message: messageToSend },
                {
                    headers: {
                        Authorization: `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            message.message = response.data.message;
            message.updated_at = response.data.updated_at;
            setIsEditing(false);
            toast.success('Message updated');
        } catch (error) {
            console.error('Error updating message:', error);
            toast.error(error.response?.data?.error || 'Failed to update message');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditedMessage(message.message);
    };

    return (
        <TooltipProvider>
            <div className={cn("flex w-full px-2", messageAlignment)}>
                <div className={cn("flex gap-1.5 max-w-[85%] items-start", flexDirection)}>
                    {/* Avatar - only show for others' messages */}
                    {showAvatar && !isOwnMessage && (
                        <Avatar
                            className="h-6 w-6 mt-0.5 shrink-0 cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
                            onClick={handleAuthorClick}
                        >
                            <AvatarFallback className="bg-primary text-primary-foreground text-[9px] font-medium">
                                {message.author.first_name[0]}{message.author.last_name[0]}
                            </AvatarFallback>
                        </Avatar>
                    )}

                    {/* Message Bubble */}
                    <div
                        onClick={handleCardClick}
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                        onTouchMove={handleTouchMove}
                        data-name={`message-card-${message.id}`}
                        data-message-id={message.id}
                        data-selected={isSelected}
                        className={cn(
                            "group relative cursor-pointer transition-all duration-150 min-w-[20vw] max-w-full",
                            showAuthor ? "mb-3" : "mb-1.5",
                            // Add margin when avatar not shown to maintain alignment (only for others' messages)
                            !showAvatar && !isOwnMessage && "ml-8",
                            // Selected state - prominent blue highlight for both light and dark mode
                            isSelected && "bg-sky-500/25 dark:bg-sky-400/30 ring-2 ring-sky-500 dark:ring-sky-400 shadow-md shadow-sky-500/20 dark:shadow-sky-400/20",
                            isFocused && !isSelected && "ring-1 ring-primary ring-offset-1",
                            isReplySelected && "ring-2 ring-primary ring-offset-2",
                            !isSelected && !isActiveThread && !isThreadOpen && "hover:brightness-95 dark:hover:brightness-110",
                            // WhatsApp-style bubble with special colors for highlights
                            isActiveThread && !isSelected
                                ? "bg-warning/30 dark:bg-warning/25 rounded-lg ring-2 ring-warning/60"
                                : isThreadOpen && !isSelected
                                    ? "bg-success/30 dark:bg-success/25 rounded-lg ring-2 ring-success/60"
                                    : !isSelected && isOwnMessage
                                        ? "bg-primary/15 dark:bg-primary/25 rounded-lg rounded-br-sm"
                                        : !isSelected && "bg-card rounded-lg rounded-bl-sm",
                            // Rounded corners for selected state
                            isSelected && "rounded-lg",
                            // Shadow for depth
                            !isSelected && "shadow-sm",
                            // Prevent overflow
                            "overflow-hidden"
                        )}
                    >
                        {/* Author Header */}
                        {showAuthor && (
                            <div className="px-2 pt-0.5 pb-0 flex items-center gap-1.5">
                                <span
                                    className="text-sm font-bold text-foreground cursor-pointer hover:underline"
                                    onClick={handleAuthorClick}
                                >
                                    {message.author.first_name} {message.author.last_name}
                                </span>
                                {message.is_forwarded && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-muted-foreground/80 text-[9px] font-medium">
                                                <Forward className="h-2.5 w-2.5" />
                                                Forwarded
                                            </span>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            This message was forwarded
                                        </TooltipContent>
                                    </Tooltip>
                                )}
                            </div>
                        )}

                        {/* Image Media */}
                        {message.media_type === 'image' && (
                            <div className="relative overflow-hidden rounded-t-lg">
                                <img
                                    src={getMediaUrl(message.media_url)}
                                    alt="Uploaded"
                                    className="w-full max-h-[180px] object-cover cursor-pointer"
                                    onClick={() => openFullscreenImage(getMediaUrl(message.media_url))}
                                />
                                <div className="absolute top-1 right-1 flex items-center gap-0.5">
                                    {message.media_size && (
                                        <span className="bg-black/60 text-white px-1.5 py-0.5 rounded text-[10px] font-medium">
                                            {formatFileSize(message.media_size)}
                                        </span>
                                    )}
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 bg-black/60 hover:bg-black/80 text-white"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDownload(message.media_url, `image_${message.id}.jpg`);
                                                }}
                                            >
                                                <Download className="h-3 w-3" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Download Image</TooltipContent>
                                    </Tooltip>
                                </div>
                            </div>
                        )}

                        {/* Video Media */}
                        {message.media_type === 'video' && (
                            <div className="relative overflow-hidden rounded-t-lg">
                                <video
                                    controls
                                    src={getMediaUrl(message.media_url)}
                                    className="w-full max-h-[180px] object-cover"
                                />
                                <div className="absolute top-1 right-1 flex items-center gap-0.5">
                                    {message.media_size && (
                                        <span className="bg-black/60 text-white px-1.5 py-0.5 rounded text-[10px] font-medium">
                                            {formatFileSize(message.media_size)}
                                        </span>
                                    )}
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 bg-black/60 hover:bg-black/80 text-white"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDownload(message.media_url, `video_${message.id}.mp4`);
                                                }}
                                            >
                                                <Download className="h-3 w-3" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Download Video</TooltipContent>
                                    </Tooltip>
                                </div>
                            </div>
                        )}

                        {/* Audio Media */}
                        {message.media_type === 'audio' && (
                            <div className="pt-1 px-2 relative">
                                <ReactAudioPlayer
                                    src={getMediaUrl(message.media_url)}
                                    controls
                                    style={{ width: '100%', height: '32px' }}
                                />
                                <div className="absolute top-1 right-2 flex items-center gap-0.5">
                                    {message.media_size && (
                                        <span className="bg-black/60 text-white px-1.5 py-0.5 rounded text-[10px] font-medium">
                                            {formatFileSize(message.media_size)}
                                        </span>
                                    )}
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 bg-black/60 hover:bg-black/80 text-white"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDownload(message.media_url, `audio_${message.id}.wav`);
                                                }}
                                            >
                                                <Download className="h-3 w-3" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Download Audio</TooltipContent>
                                    </Tooltip>
                                </div>
                            </div>
                        )}

                        {/* Other File Types (PDF, DOCX, etc.) */}
                        {message.media_type === 'other' && message.media_url && (
                            <div className="pt-1 px-2">
                                <div
                                    className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors cursor-pointer group"
                                    onClick={() => handleDownload(message.media_url, getFileName(message.media_url))}
                                >
                                    <div className={cn("w-10 h-10 rounded flex items-center justify-center shrink-0", getFileTypeInfo(message.media_url).color)}>
                                        <FileText className="h-5 w-5 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-foreground truncate">
                                            {getFileName(message.media_url)}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            {getFileTypeInfo(message.media_url).text}
                                            {message.media_size && ` • ${formatFileSize(message.media_size)}`}
                                            {' • Click to download'}
                                        </div>
                                    </div>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDownload(message.media_url, getFileName(message.media_url));
                                                }}
                                            >
                                                <Download className="h-4 w-4" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Download File</TooltipContent>
                                    </Tooltip>
                                </div>
                            </div>
                        )}

                        {/* Content */}
                        <div className={cn(
                            "relative px-2 flex flex-col",
                            showAuthor ? "pt-0" : "py-1",
                            !showAuthor && "min-h-[24px]",
                            // Add bottom padding when there are replies to create gap
                            !isThread && message.reply_count > 0 && "pb-6"
                        )}>
                            {/* Quoted Reply (WhatsApp-style) - Only for thread messages */}
                            {isThread && message.replied_to_message && (
                                <div
                                    className={cn(
                                        "mb-2.5 px-3 py-2 rounded-md border-l-4 cursor-pointer transition-all",
                                        "shadow-sm hover:shadow-md hover:scale-[1.01]",
                                        isOwnMessage
                                            ? "bg-primary/30 border-primary dark:bg-primary/20 hover:bg-primary/40"
                                            : "bg-muted border-muted-foreground/50 hover:bg-muted/80"
                                    )}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (onScrollToMessage && message.replied_to_message?.id) {
                                            // Ensure we pass the correct message ID
                                            const targetMessageId = message.replied_to_message.id;
                                            onScrollToMessage(targetMessageId);
                                        }
                                    }}
                                >
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <span className="text-[11px] font-bold text-foreground">
                                            ↩ {message.replied_to_message.author.first_name} {message.replied_to_message.author.last_name}
                                        </span>
                                    </div>
                                    <div className="text-[12px] text-foreground/80 line-clamp-2 font-medium">
                                        {message.replied_to_message.media_type && (
                                            <span className="italic">
                                                {message.replied_to_message.media_type === 'image' && '📷 Image'}
                                                {message.replied_to_message.media_type === 'video' && '🎥 Video'}
                                                {message.replied_to_message.media_type === 'audio' && '🎤 Audio'}
                                                {message.replied_to_message.media_type === 'other' && '📎 File'}
                                                {message.replied_to_message.message ? ': ' : ''}
                                            </span>
                                        )}
                                        {convertToEmojis(message.replied_to_message.message) || ''}
                                    </div>
                                </div>
                            )}

                            {/* Message text */}
                            <div className="break-words max-w-full" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', display: 'block' }}>
                                {/* Show forwarded tag when author is not shown */}
                                {!showAuthor && message.is_forwarded && (
                                    <div className="mb-1">
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-muted-foreground/80 text-[9px] font-medium">
                                                    <Forward className="h-2.5 w-2.5" />
                                                    Forwarded
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                This message was forwarded
                                            </TooltipContent>
                                        </Tooltip>
                                    </div>
                                )}
                                <MarkdownText className="text-[13px] leading-snug text-foreground break-words">{convertToEmojis(message.message)}</MarkdownText>
                                {/* Show edited inline only when no replies */}
                                {isMessageEdited() && (!message.reply_count || message.reply_count === 0 || isThread) && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <span className="text-[9px] text-muted-foreground italic ml-1">
                                                (edited)
                                            </span>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            Edited {new Date(message.updated_at).toLocaleString()}
                                        </TooltipContent>
                                    </Tooltip>
                                )}
                                {/* Inline spacer for timestamp */}
                                <span className="inline-block w-12"></span>
                            </div>

                            {/* Timestamp and link status - WhatsApp style bottom right */}
                            <div className="absolute bottom-0.5 right-1.5 flex items-center gap-0.5">
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span className="text-[10px] text-muted-foreground/80 cursor-default">
                                            {getRelativeTime(message.created_at)}
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        {new Date(message.created_at).toLocaleString()}
                                    </TooltipContent>
                                </Tooltip>
                                {!mobileScreen && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <span className="flex items-center">
                                                {message.action_taken
                                                    ? <Link2 className="h-2.5 w-2.5 text-primary/70" />
                                                    : <Unlink className="h-2.5 w-2.5 text-orange-400/70" />
                                                }
                                            </span>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {message.action_taken ? "Linked to Item" : "Not Linked"}
                                        </TooltipContent>
                                    </Tooltip>
                                )}
                            </div>

                            {/* Reply count and edited indicator */}
                            {!isThread && message.reply_count > 0 && (
                                <span className="absolute bottom-1 left-1.5 text-[11px] text-primary font-semibold flex items-center gap-1.5 bg-primary/10 px-1.5 py-0.5 rounded-full">
                                    <Reply className="h-3 w-3" />
                                    {message.reply_count} {message.reply_count === 1 ? 'reply' : 'replies'}
                                    {isMessageEdited() && (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span className="text-muted-foreground italic">
                                                    (edited)
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                Edited {new Date(message.updated_at).toLocaleString()}
                                            </TooltipContent>
                                        </Tooltip>
                                    )}
                                </span>
                            )}
                        </div>

                        {/* Action buttons - Right top corner of message bubble */}
                        <div className={cn(
                            "absolute top-1 right-1 flex items-center gap-0.5 z-10",
                            mobileScreen ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                        )}>
                            {/* Reply button - Only for thread messages */}
                            {isThread && onReply && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className={cn(
                                                "h-5 w-5 bg-background/80 hover:bg-background shadow-sm",
                                                isReplySelected && "opacity-100 text-primary"
                                            )}
                                            onClick={handleReplyClick}
                                        >
                                            <Reply className="h-2.5 w-2.5" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Reply to this message</TooltipContent>
                                </Tooltip>
                            )}

                            {/* Edit button - Only for own messages */}
                            {isOwnMessage && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-5 w-5 bg-background/80 hover:bg-background shadow-sm"
                                            onClick={handleEditClick}
                                        >
                                            <Pencil className="h-2.5 w-2.5" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Edit message</TooltipContent>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Dialog for Desktop */}
            {!mobileScreen && (
                <Dialog open={isEditing} onOpenChange={(open) => !open && handleCancelEdit()}>
                    <DialogContent className="sm:max-w-[70vw]">
                        <DialogHeader>
                            <DialogTitle>Edit Message</DialogTitle>
                        </DialogHeader>
                        <div className="relative">
                            {/* Mention suggestions */}
                            {showMentionSuggestions && mentionSuggestions.length > 0 && (
                                <div className="absolute bottom-full left-0 right-0 mb-1 bg-popover border rounded-lg shadow-lg max-h-[200px] overflow-y-auto z-50">
                                    {mentionSuggestions.map((user, index) => (
                                        <div
                                            key={user.id}
                                            className={cn(
                                                "flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors first:rounded-t-lg last:rounded-b-lg",
                                                index === selectedMentionIndex ? "bg-accent" : "hover:bg-muted/50"
                                            )}
                                            onClick={() => handleMentionSelect(user)}
                                        >
                                            <Avatar className="h-8 w-8 ring-2 ring-background">
                                                <AvatarImage src={user.picture} />
                                                <AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
                                                    {user.first_name?.[0] || user.username[0]}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className="flex flex-col min-w-0">
                                                <span className="text-sm font-medium truncate">{user.username}</span>
                                                <span className="text-xs text-muted-foreground truncate">
                                                    {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <Textarea
                                ref={editInputRef}
                                value={editedMessage}
                                onChange={handleEditTextChange}
                                onKeyDown={handleEditKeyDown}
                                autoFocus
                                rows={3}
                                className="resize-none max-h-[280px] overflow-y-auto"
                                disabled={isUpdating}
                            />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={handleCancelEdit} disabled={isUpdating}>
                                Cancel
                            </Button>
                            <Button onClick={handleSaveEdit} disabled={isUpdating}>
                                {isUpdating ? 'Saving...' : 'Save'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            {/* Edit Drawer for Mobile */}
            {mobileScreen && (
                <Sheet open={isEditing} onOpenChange={(open) => !open && handleCancelEdit()}>
                    <SheetContent side="bottom" className="rounded-t-xl">
                        <SheetHeader>
                            <SheetTitle>Edit Message</SheetTitle>
                        </SheetHeader>
                        <div className="px-4 py-2 relative">
                            {/* Mention suggestions */}
                            {showMentionSuggestions && mentionSuggestions.length > 0 && (
                                <div className="absolute bottom-full left-4 right-4 mb-1 bg-popover border rounded-lg shadow-lg max-h-[200px] overflow-y-auto z-50">
                                    {mentionSuggestions.map((user, index) => (
                                        <div
                                            key={user.id}
                                            className={cn(
                                                "flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors first:rounded-t-lg last:rounded-b-lg",
                                                index === selectedMentionIndex ? "bg-accent" : "hover:bg-muted/50"
                                            )}
                                            onClick={() => handleMentionSelect(user)}
                                        >
                                            <Avatar className="h-8 w-8 ring-2 ring-background">
                                                <AvatarImage src={user.picture} />
                                                <AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
                                                    {user.first_name?.[0] || user.username[0]}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className="flex flex-col min-w-0">
                                                <span className="text-sm font-medium truncate">{user.username}</span>
                                                <span className="text-xs text-muted-foreground truncate">
                                                    {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <Textarea
                                ref={editInputRef}
                                value={editedMessage}
                                onChange={handleEditTextChange}
                                onKeyDown={handleEditKeyDown}
                                autoFocus
                                rows={3}
                                className="resize-none max-h-[280px] overflow-y-auto"
                                disabled={isUpdating}
                            />
                        </div>
                        <SheetFooter className="flex-row gap-2">
                            <Button variant="outline" onClick={handleCancelEdit} disabled={isUpdating} className="flex-1">
                                Cancel
                            </Button>
                            <Button onClick={handleSaveEdit} disabled={isUpdating} className="flex-1">
                                {isUpdating ? 'Saving...' : 'Save'}
                            </Button>
                        </SheetFooter>
                    </SheetContent>
                </Sheet>
            )}
        </TooltipProvider>
    );
};

export default MessageCard;
